import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Switch } from "antd";
import Title from "antd/es/typography/Title";
import { LockOutlined, MenuOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RankingAPI } from "../../API/RankingAPI";
import {
  extractPreferences,
  toFrozenMap,
  fromApiColumnLayoutKey,
  toApiColumnLayoutKey,
} from "../DndTable/gridPreferencesUtils";
import { getColumnDisplayLabel } from "../../screens/Rankings/RankingsUtils";

const READ_ONLY_COLUMNS = ["spec_type", "card_no"];

function FrozenBadge({ frozen }) {
  if (!frozen) return null;

  return (
    <span className="field-item-frozen-badge" title="Frozen column">
      <LockOutlined />
      Frozen
    </span>
  );
}

function SortableRow({ item, onToggle }) {
  return (
    <div className={"field-item field-item-active"}>
      <button
        aria-label={`Drag handle for ${item.label}`}
        className="field-item-grip-btn"
        type="button"
      >
        <MenuOutlined
          style={{
            touchAction: "none",
            cursor: "move",
          }}
        />
      </button>
      <p className="field-item-label">{item.label}</p>
      <FrozenBadge frozen={item.frozen} />
      <Switch
        size="small"
        id={`sw-${item.id}`}
        checked={item.rank !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
        disabled={READ_ONLY_COLUMNS.includes(item.id) || item.frozen}
      />
    </div>
  );
}

// Memoized: a toggle or reorder only changes the rows it touches, so the rest
// keep their item identity and skip re-rendering.
const SortableItem = memo(function SortableItem({ item, onToggle }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: item.rank === null });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.4 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="field-item">
      <button
        ref={setActivatorNodeRef}
        aria-label={`Drag handle for ${item.label}`}
        className="field-item-grip-btn"
        {...attributes}
        {...listeners}
        type="button"
        tabIndex={-1}
      >
        <MenuOutlined
          style={{
            touchAction: "none",
            cursor: "move",
          }}
        />
      </button>

      <p
        className="field-item-label"
        title={
          item.frozen
            ? "Drag to reorder within the frozen columns"
            : "Drag to reorder"
        }
      >
        {item.label}
      </p>

      <FrozenBadge frozen={item.frozen} />

      <Switch
        size="small"
        id={`sw-${item.id}`}
        checked={item.rank !== null}
        onChange={() => onToggle(item.id)}
        className="field-item-switch"
        disabled={READ_ONLY_COLUMNS.includes(item.id) || item.frozen}
      />
    </div>
  );
});

export const SelectColumns = ({
  open,
  setOpen,
  messageToast,
  setSelectedColumns,
}) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 },
    }),
    useSensor(KeyboardSensor),
  );

  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const scrollerRef = useRef(null);
  const dragStartScrollRef = useRef(0);

  const fetchColumnConfig = useCallback(async () => {
    setSpinning(true);
    try {
      const [response, preferencesResponse] = await Promise.all([
        RankingAPI.getColumnConfig(),
        RankingAPI.getRankGridPreferences(),
      ]);

      if (response?.data?.status === true) {
        const frozenMap = toFrozenMap(
          extractPreferences(preferencesResponse).column_layout,
        );

        const configColumns = response.data.all_columns
          // Only the internal drag handle and the assigned-on timestamp stay
          // out of the managed set. Status, Comments and Assigned To are now
          // reorderable / hideable like any other column.
          .filter(
            (col) =>
              col.column_key !== "is_drag" &&
              col.column_key !== "assigned_on",
          )
          .map((col) => {
            const existCol = response.data?.visible_columns?.find(
              (c) => c.column_key === col.column_key,
            );
            // Grid uses "comments"; the backend key is "have_comments".
            const gridKey = fromApiColumnLayoutKey(col.column_key);
            return {
              id: gridKey,
              label:
                col.column_key === "have_comments"
                  ? "Comments"
                  : getColumnDisplayLabel(col.column_key, col.label),
              rank: existCol ? existCol.display_order : null,
              // Keyed by grid key, not the API one -- toFrozenMap() already
              // converted them, so "have_comments" would never match.
              frozen: Boolean(frozenMap[gridKey]),
            };
          })
          .sort((a, b) => {
            const orderA =
              a.rank !== null ? parseInt(a.rank) : Number.MAX_SAFE_INTEGER;
            const orderB =
              b.rank !== null ? parseInt(b.rank) : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });

        // The grid renders frozen columns as a contiguous left block, so list
        // them first here too and renumber ranks to match what the grid shows.
        const shownColumns = configColumns.filter((col) => col.rank !== null);
        const hiddenColumns = configColumns.filter((col) => col.rank === null);
        const orderedShown = [
          ...shownColumns.filter((col) => col.frozen),
          ...shownColumns.filter((col) => !col.frozen),
        ].map((col, index) => ({ ...col, rank: index + 1 }));

        setItems([...orderedShown, ...hiddenColumns]);
      }
    } catch (error) {
      messageToast("error", "Failed to fetch column configuration.");
    } finally {
      setSpinning(false);
    }
  }, [messageToast]);

  useEffect(() => {
    if (open) {
      fetchColumnConfig();
    }
  }, [open, fetchColumnConfig]);

  const shown = useMemo(
    () => items.filter((i) => i.rank !== null).sort((a, b) => a.rank - b.rank),
    [items],
  );
  const hidden = useMemo(() => items.filter((i) => i.rank === null), [items]);

  const shownIds = useMemo(() => shown.map((i) => i.id), [shown]);

  // Rows that keep their rank keep their object identity too, so the memoized
  // SortableItem can skip them on a reorder.
  const reassignRanks = (list) =>
    list.map((item, idx) =>
      item.rank === idx + 1 ? item : { ...item, rank: idx + 1 },
    );

  const activeItem = items.find((i) => i.id === activeId);

  function handleDragStart(event) {
    dragStartScrollRef.current = scrollerRef.current?.scrollTop ?? 0;
    setActiveId(event.active.id);
  }

  // A rejected drop leaves the row at its original slot, but auto-scroll has
  // usually carried the list away from it. The drag overlay is position: fixed
  // so nothing clips it, and the drop animation would tween up to that
  // off-screen slot, flying across the modal on the way. Restoring the scroll
  // position first puts the slot back on screen, so the animation plays out
  // inside the list. Must run synchronously here -- dnd-kit measures the
  // animation's endpoint in a layout effect straight after this.
  const restoreDragStartScroll = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = dragStartScrollRef.current;
    }
  };

  function handleDragCancel() {
    setActiveId(null);
    restoreDragStartScroll();
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);

    const oldIndex = shown.findIndex((i) => i.id === active.id);
    const newIndex = over ? shown.findIndex((i) => i.id === over.id) : -1;

    // Frozen columns are a contiguous block at the top: reorder freely within
    // either block, but never across the frozen / unfrozen boundary.
    const accepted =
      oldIndex !== -1 &&
      newIndex !== -1 &&
      oldIndex !== newIndex &&
      shown[oldIndex].frozen === shown[newIndex].frozen;

    if (!accepted) {
      restoreDragStartScroll();
      return;
    }

    const reordered = arrayMove(shown, oldIndex, newIndex);

    const updatedShown = reassignRanks(reordered);
    setItems([...updatedShown, ...hidden]);
  }

  // Stable identity so the memoized SortableItem isn't invalidated every render.
  const toggleItem = useCallback((id) => {
    setItems((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        if (i.rank !== null) return { ...i, rank: null };
        const maxRank = Math.max(0, ...prev.map((j) => j.rank || 0));
        return { ...i, rank: maxRank + 1 };
      }),
    );
  }, []);

  function applyFun() {
    setSpinning(true);
    const bodyData = {
      columns: items
        .filter((i) => i.rank !== null)
        // Map the grid key back to the API key (comments -> have_comments).
        .map((i) => ({
          column_key: toApiColumnLayoutKey(i.id),
          display_order: i.rank,
        })),
    };
    RankingAPI.updateColumnConfig(bodyData)
      .then((response) => {
        if (response?.data?.status === true) {
          setSelectedColumns(items.filter((i) => i.rank !== null));
          messageToast("success", "Column configuration updated successfully.");
        } else {
          messageToast("error", "Failed to update column configuration.");
        }
      })
      .catch((error) => {
        messageToast("error", "Failed to update column configuration.");
      })
      .finally(() => {
        setSpinning(false);
        setOpen(false);
      });
  }

  return (
    <Modal
      open={open}
      okText="Apply"
      cancelText="Clear All"
      onCancel={() => setOpen(false)}
      onOk={applyFun}
      style={{
        minWidth: "800px",
      }}
      footer={[
        <Button key="back" type="text" onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={applyFun}
          loading={spinning}
        >
          Apply
        </Button>,
      ]}
    >
      <div style={{ padding: "25px" }}>
        <div
          style={{ marginBottom: "20px", borderBottom: "solid 1px #80808047" }}
        >
          <Title level={5}>Add Columns</Title>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "20px",
          }}
        >
          <div>
            <p className="items-section-title">Shown</p>
            <div className="fields-overlay-drag-container" ref={scrollerRef}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
                modifiers={[
                  restrictToVerticalAxis,
                  restrictToFirstScrollableAncestor,
                ]}
              >
                <SortableContext
                  items={shownIds}
                  strategy={verticalListSortingStrategy}
                >
                  <div>
                    {shown.map((item) => (
                      <SortableItem
                        key={item.id}
                        item={item}
                        onToggle={toggleItem}
                      />
                    ))}
                    {shown.length === 0 && (
                      <p className="empty-item">
                        No enabled items. Toggle some back on below.
                      </p>
                    )}
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeItem ? (
                    <SortableRow item={activeItem} onToggle={toggleItem} />
                  ) : null}
                </DragOverlay>
              </DndContext>
            </div>
          </div>

          <div className="mt-1">
            <p className="items-section-title">Hidden</p>
            <div className="fields-overlay-drag-container">
              {hidden.length ? (
                hidden.map((item) => (
                  <div key={item.id} className="field-item">
                    <div className="field-item-label">{item.label}</div>
                    <Switch
                      size="small"
                      id={`sw-${item.id}`}
                      checked={item.rank !== null}
                      onChange={() => toggleItem(item.id)}
                      className="field-item-switch"
                      disabled={READ_ONLY_COLUMNS.includes(item.id) || item.frozen}
                    />
                  </div>
                ))
              ) : (
                <p className="empty-item">No hidden items.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
